/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Stack, Divider } from '@mui/material';
import { VisibilityOutlined } from '@mui/icons-material';
import { DrawerContent } from 'components/common/drawer/DrawerContent';
import { EntityFileDetails } from 'components/shared/EntityFileDetails';
import { shareList } from 'store/slices/files';
import { fileTypeChecks } from 'utils/file-icon';
import { formatToString } from 'utils/date-format';
import { useFile } from 'hooks/useFile';
import { useFileDrawers } from 'hooks/useFileDrawers';
import { CreateOutlined, DeleteOutlineOutlined, FileCopyOutlined, FileDownloadOutlined } from '@mui/icons-material';

export const SharedFileDetails = (props) => {
  const { close } = props;

  const dispatch = useDispatch();

  const { handleDeleteFile, handleDownloadFile, handleDuplicateFile, handleEditArtwork, handleGoToMarketplace } =
    useFile();
  const { openDrawerRenameFile } = useFileDrawers();

  const selectedFile = useSelector((state) => state.files.selectedFile);
  const isFile = useMemo(() => selectedFile?.format === 'File', [selectedFile]);
  const isFolder = useMemo(() => selectedFile?.format === 'Folder', [selectedFile]);
  const isArtwork = useMemo(() => fileTypeChecks.isFileArtwork(selectedFile), [selectedFile]);
  const isQuote = useMemo(() => fileTypeChecks.isFileQuote(selectedFile), [selectedFile]);

  const sharedUserList = useSelector((state) => state.files.shareList.value);
  const fileOwner = useMemo(() => {
    if (selectedFile?.ownedBy?.name) {
      return selectedFile?.ownedBy?.name;
    }
    if (selectedFile?.origin === 'mine' || (!selectedFile.origin && !selectedFile.ownedBy)) {
      return 'Me';
    }
    return '';
  }, [selectedFile]);
  const hasWriteAccess = useMemo(() => {
    return selectedFile?.access && selectedFile.access.includes('Write');
  }, [selectedFile]);

  useEffect(() => {
    if (selectedFile?.id) {
      dispatch(shareList({ fileId: selectedFile.id }));
    }
  }, [dispatch, selectedFile]);

  return (
    <DrawerContent>
      <DrawerContent.Header title={selectedFile?.name} />
      <DrawerContent.Wrapper>
        <DrawerContent.Content>
          <EntityFileDetails>
            {!selectedFile?.id && <EntityFileDetails.EmptyFile />}
            {selectedFile?.id && (
              <>
                <EntityFileDetails.FileType file={selectedFile} />
                {sharedUserList.length > 0 && (
                  <>
                    <EntityFileDetails.PeopleWithAccess users={sharedUserList} />
                    <Divider />
                  </>
                )}
                <EntityFileDetails.Details title="Details">
                  {selectedFile?.format && (
                    <EntityFileDetails.DetailsItem
                      title="Type"
                      subtitle={selectedFile?.format}
                    />
                  )}
                  {fileOwner && (
                    <EntityFileDetails.DetailsItem
                      title="Owner"
                      subtitle={fileOwner}
                    />
                  )}
                  {selectedFile?.last_modified && (
                    <EntityFileDetails.DetailsItem
                      title="Modified"
                      subtitle={formatToString(selectedFile?.last_modified)}
                    />
                  )}
                  {selectedFile?.createdOn && (
                    <EntityFileDetails.DetailsItem
                      title="Created"
                      subtitle={formatToString(selectedFile?.createdOn)}
                    />
                  )}
                </EntityFileDetails.Details>
                <Divider />
                <Stack
                  direction="column"
                  spacing={1.5}
                >
                  {/* Edit Artwork */}
                  {isArtwork && hasWriteAccess && (
                    <EntityFileDetails.EditButton
                      title="Edit Artwork"
                      action={() => handleEditArtwork(selectedFile)}
                    />
                  )}
                  {/* Rename File */}
                  {hasWriteAccess && (
                    <EntityFileDetails.ActionButton
                      title={`Rename ${selectedFile?.format}`}
                      icon={<CreateOutlined sx={{ fontSize: '22px' }} />}
                      action={openDrawerRenameFile}
                    />
                  )}
                  {/* Download File */}
                  {isFile && !isQuote && !isArtwork && !isFolder && (
                    <EntityFileDetails.ActionButton
                      title="Download File"
                      icon={<FileDownloadOutlined sx={{ fontSize: '22px' }} />}
                      action={() => handleDownloadFile(selectedFile)}
                    />
                  )}
                  {/* Copy File */}
                  {hasWriteAccess && !isFolder && !isQuote && (
                    <EntityFileDetails.ActionButton
                      title="Make a Copy"
                      icon={<FileCopyOutlined sx={{ fontSize: '22px' }} />}
                      action={() => handleDuplicateFile(selectedFile)}
                    />
                  )}

                  {/* Delete File */}
                  {hasWriteAccess && (
                    <EntityFileDetails.ActionButton
                      title={`Delete ${selectedFile?.format}`}
                      icon={<DeleteOutlineOutlined sx={{ fontSize: '22px' }} />}
                      action={() => handleDeleteFile(selectedFile, close)}
                    />
                  )}

                  {/* View in Marketplace */}
                  {isQuote && (
                    <EntityFileDetails.ActionButton
                      title="View in Marketplace"
                      icon={<VisibilityOutlined sx={{ fontSize: '22px' }} />}
                      action={() => handleGoToMarketplace(selectedFile)}
                    />
                  )}
                </Stack>
              </>
            )}
          </EntityFileDetails>
        </DrawerContent.Content>
      </DrawerContent.Wrapper>
    </DrawerContent>
  );
};
